import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { getTrackCoverUrl, handleImageError } from "@/utils/trackCoverUtils";
import { AudioLines, User } from "lucide-react";
import { useNavigate } from "react-router-dom";

interface ArtistPreviewLimitDialogProps {
  open: boolean;
  onDismiss: () => void;
  trackTitle?: string;
  artistName?: string;
  coverUrl?: string;
  artistUsername?: string;
  isSelfStream: boolean;
}

export default function ArtistPreviewLimitDialog({
  open,
  onDismiss,
  trackTitle,
  artistName,
  coverUrl,
  artistUsername,
  isSelfStream,
}: ArtistPreviewLimitDialogProps) {
  const navigate = useNavigate();
  const resolvedCover = getTrackCoverUrl(coverUrl, "medium");

  const handleVisitProfile = () => {
    onDismiss();
    if (artistUsername) {
      navigate(`/profiles/${artistUsername}`);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onDismiss()}>
      <DialogContent
        hideCloseButton
        className="light font-archivo max-w-[95vw] sm:max-w-[480px] gap-0 overflow-hidden rounded-[3px] border border-[#1E1B4B]/[0.12] bg-white p-0 text-center"
      >
        <DialogTitle className="sr-only">Preview ended</DialogTitle>
        <div className="px-7 pb-[26px] pt-[30px]">
          <img
            src={resolvedCover}
            alt={trackTitle ?? "Track cover"}
            className="mx-auto h-40 w-40 rounded-[3px] bg-[#EFEFF7] object-cover"
            onError={handleImageError}
          />
          <div className="mt-[18px] flex items-center justify-center gap-2 font-archivo text-[19px] font-bold leading-[1.25] text-[#1E1B4B]">
            <AudioLines className="h-[17px] w-[17px] text-[#5A31D8]" strokeWidth={1.8} />
            {trackTitle ?? "Preview ended"}
          </div>
          {artistName && (
            <div className="mt-[7px] font-archivo text-sm text-[#6E6B8A]">
              {artistName}
            </div>
          )}
          <div className="mx-auto mt-[14px] max-w-[42ch] font-archivo text-sm leading-[1.6] text-[#4A4770]">
            {isSelfStream
              ? "Artists cannot generate valid streams for their own tracks. Your 30-second preview has ended."
              : "Your 30-second preview of this track has ended."}
          </div>
          <div className="mt-[22px] flex flex-col gap-[9px]">
            {artistUsername && (
              <button
                type="button"
                onClick={handleVisitProfile}
                className="flex items-center justify-center gap-2 rounded-[2px] bg-[#5A31D8] py-4 font-archivo text-[15px] font-bold text-white hover:bg-[#4526A8]"
              >
                <User className="h-4 w-4" strokeWidth={1.8} />
                Visit Profile
              </button>
            )}
            <button
              type="button"
              onClick={onDismiss}
              className="flex items-center justify-center py-[14px] font-archivo text-sm font-medium text-[#6E6B8A] hover:text-[#4A4770]"
            >
              Dismiss
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
